import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { formatGHS, formatUSD } from "@/lib/format";
import { ShieldCheck, ChartLineUp, EnvelopeOpen, Package, ArrowsClockwise } from "@phosphor-icons/react";

export default function AdminPage() {
  const [token, setToken] = useState(sessionStorage.getItem("obrasafe_admin_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authedClient = () =>
    axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });

  const loadAll = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const c = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
      const [s, l, o, p, prods, refs, pays] = await Promise.all([
        c.get("/admin/stats"),
        c.get("/admin/leads"),
        c.get("/admin/orders"),
        c.get("/admin/payments"),
        axios.get(`${API}/products`),
        c.get("/admin/referrals"),
        c.get("/admin/referral-payouts"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setOrders(o.data);
      setPayments(p.data);
      setProducts(prods.data);
      setReferrers(refs.data);
      setPayouts(pays.data);
    } catch (e) {
      setError("Invalid token or server error");
      sessionStorage.removeItem("obrasafe_admin_token");
      setToken("");
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await authedClient().patch(`/admin/orders/${orderId}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(`Order marked as ${status}`);
    } catch {
      toast.error("Failed to update order status");
    }
  };

  const updateStock = async (productId, stockQty) => {
    try {
      const { data } = await authedClient().patch(`/admin/products/${productId}/stock`, { stock_qty: stockQty });
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, stock_qty: data.stock_qty, in_stock: data.in_stock } : p))
      );
      toast.success(`Stock updated: ${stockQty} left`);
    } catch {
      toast.error("Failed to update stock");
    }
  };

  const markPayoutPaid = async (payoutId) => {
    try {
      const { data } = await authedClient().post(`/admin/referral-payouts/${payoutId}/mark-paid`);
      toast.success(`Marked GH₵${data.amount.toFixed(2)} as paid in cash`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    }
  };

  const settleReferrer = async (referrerId) => {
    try {
      const { data } = await authedClient().post(`/admin/referrals/${referrerId}/settle`);
      toast.success(`Settled ${data.settled_count} payout(s) — GH₵${data.settled_total.toFixed(2)} paid in cash`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Settle failed");
    }
  };

  const emailPayoutList = async () => {
    try {
      const { data } = await authedClient().post("/admin/payout-list/send");
      toast.success(`Payout list emailed — ${data.referrers} referrer(s), GH₵${data.total.toFixed(2)} total`);
    } catch {
      toast.error("Failed to send payout list");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6" data-testid="admin-login">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sessionStorage.setItem("obrasafe_admin_token", tokenInput.trim());
            setToken(tokenInput.trim());
          }}
          className="w-full max-w-md border border-white/10 bg-[#141414] p-8"
        >
          <ShieldCheck size={36} weight="duotone" className="text-[#FFC107] mb-4" />
          <h1 className="font-display text-3xl font-black uppercase mb-2">Admin Access</h1>
          <p className="text-zinc-400 text-sm mb-6">Enter the admin token to view leads, orders, and payments.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="w-full h-12 px-4 bg-[#0a0a0a] border border-white/15 focus:border-[#FFC107] outline-none mb-4"
          />
          {error && <p className="text-[#D32F2F] text-sm mb-3">{error}</p>}
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full h-12 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight">Control Room</h1>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={loadAll}
              className="h-10 px-4 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] text-xs uppercase tracking-wider font-bold flex items-center gap-2"
            >
              <ArrowsClockwise size={14} weight="bold" /> Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => {
                sessionStorage.removeItem("obrasafe_admin_token");
                setToken("");
              }}
              className="h-10 px-4 border border-white/20 hover:border-[#D32F2F] hover:text-[#D32F2F] text-xs uppercase tracking-wider font-bold"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={EnvelopeOpen} label="Leads" value={stats?.leads ?? 0} />
          <StatCard icon={Package} label="Orders" value={stats?.orders ?? 0} />
          <StatCard icon={ChartLineUp} label="Paid orders" value={stats?.paid_orders ?? 0} />
          <StatCard
            icon={ChartLineUp}
            label="Revenue (GHS)"
            value={stats?.revenue?.GHS ? formatGHS(stats.revenue.GHS) : "GHS 0"}
          />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          {["dashboard", "inventory", "leads", "orders", "payments", "referrals"].map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`h-10 px-5 text-xs uppercase tracking-[0.18em] font-bold transition-all ${
                tab === t ? "bg-[#FFC107] text-black" : "border border-white/15 hover:border-white/40"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading && <div className="text-zinc-400">Loading…</div>}

        {tab === "inventory" && (
          <Table
            testId="admin-inventory-table"
            headers={["Product", "Category", "Price", "Stock left", "Status"]}
            rows={products.map((p) => [
              <div key={`n-${p.id}`} className="flex items-center gap-3">
                <img src={p.image_url} alt="" className="w-10 h-10 object-cover border border-white/10 bg-black" />
                <span className="font-bold">{p.name}</span>
              </div>,
              p.category,
              formatGHS(p.price_ghs),
              <StockEditor key={`s-${p.id}`} product={p} onSave={updateStock} />,
              <span
                key={`st-${p.id}`}
                data-testid={`stock-status-${p.id}`}
                className={`text-xs uppercase tracking-wider font-bold ${
                  (p.stock_qty ?? 0) === 0
                    ? "text-[#D32F2F]"
                    : (p.stock_qty ?? 0) <= 5
                    ? "text-[#FFC107]"
                    : "text-green-400"
                }`}
              >
                {(p.stock_qty ?? 0) === 0 ? "Out of stock" : (p.stock_qty ?? 0) <= 5 ? "Low stock" : "In stock"}
              </span>,
            ])}
          />
        )}

        {tab === "leads" && (
          <Table
            testId="admin-leads-table"
            headers={["Date", "Type", "Name", "Email", "Phone", "Subject/Product", "Message"]}
            rows={leads.map((l) => [
              new Date(l.created_at).toLocaleString(),
              l.type,
              l.name,
              l.email,
              l.phone,
              l.subject || l.product_name || "-",
              l.message,
            ])}
          />
        )}
        {tab === "orders" && (
          <Table
            testId="admin-orders-table"
            headers={["Date", "Order ID", "Customer", "Phone", "Total", "Method", "Payment", "Fulfilment"]}
            rows={orders.map((o) => [
              new Date(o.created_at).toLocaleString(),
              o.id.slice(0, 12) + "…",
              o.customer?.full_name,
              o.customer?.phone,
              o.currency === "GHS" ? formatGHS(o.total) : formatUSD(o.total),
              o.payment_method,
              o.payment_status,
              <OrderStatusSelect key={o.id} order={o} onChange={updateOrderStatus} />,
            ])}
          />
        )}
        {tab === "payments" && (
          <Table
            testId="admin-payments-table"
            headers={["Date", "Provider", "Reference/Session", "Amount", "Currency", "Status"]}
            rows={payments.map((p) => [
              new Date(p.created_at).toLocaleString(),
              p.provider,
              p.session_id?.slice(0, 24) + "…",
              p.amount?.toFixed(2),
              p.currency,
              p.payment_status,
            ])}
          />
        )}
        {tab === "referrals" && (
          <div className="space-y-8">
            <button
              data-testid="email-payout-list-btn"
              onClick={emailPayoutList}
              className="h-10 px-5 border border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107] hover:text-black text-xs uppercase tracking-wider font-black transition-colors"
            >
              Email me the payout list now
            </button>
            <div>
              <h3 className="font-display text-lg font-black uppercase mb-3 text-[#FFC107]">// Referrers</h3>
              <Table
                testId="admin-referrers-table"
                headers={["Joined", "Name", "Code", "Phone", "Network", "Referrals", "Earned", "Paid out", "Owed", "Weekly payout"]}
                rows={referrers.map((r) => {
                  const owed = payouts
                    .filter((p) => p.referrer_id === r.id && p.status === "pending")
                    .reduce((s, p) => s + p.amount, 0);
                  return [
                    new Date(r.created_at).toLocaleDateString(),
                    r.name,
                    <span key={r.id} className="font-mono-accent text-[#FFC107]">{r.code}</span>,
                    r.phone,
                    r.network,
                    r.referral_count ?? 0,
                    formatGHS(r.total_earned ?? 0),
                    formatGHS(r.total_paid ?? 0),
                    <span key={`o-${r.id}`} data-testid={`referrer-owed-${r.id}`} className={owed > 0 ? "font-bold text-[#FFC107]" : "text-zinc-500"}>
                      {formatGHS(owed)}
                    </span>,
                    owed > 0 ? (
                      <button
                        key={`st-${r.id}`}
                        data-testid={`settle-referrer-${r.id}`}
                        onClick={() => settleReferrer(r.id)}
                        className="h-8 px-3 bg-[#FFC107] hover:bg-[#FFB300] text-black text-xs uppercase tracking-wider font-black"
                      >
                        Settle {formatGHS(owed)}
                      </button>
                    ) : (
                      "—"
                    ),
                  ];
                })}
              />
            </div>
            <div>
              <h3 className="font-display text-lg font-black uppercase mb-3 text-[#FFC107]">// Commission log</h3>
              <Table
                testId="admin-payouts-table"
                headers={["Date", "Referrer", "Code", "Phone", "Order total", "5% Commission", "Status", "Action"]}
                rows={payouts.map((p) => [
                  new Date(p.created_at).toLocaleString(),
                  p.referrer_name,
                  <span key={`c-${p.id}`} className="font-mono-accent">{p.referrer_code}</span>,
                  `${p.momo_phone} (${p.network})`,
                  formatGHS(p.order_total),
                  <span key={`a-${p.id}`} className="font-bold text-[#FFC107]">{formatGHS(p.amount)}</span>,
                  <span
                    key={`s-${p.id}`}
                    data-testid={`payout-status-${p.id}`}
                    title={p.reason || ""}
                    className={`text-xs uppercase tracking-wider font-bold ${
                      p.status === "paid"
                        ? "text-green-400"
                        : p.status === "blocked"
                        ? "text-zinc-500"
                        : "text-[#FFC107]"
                    }`}
                  >
                    {p.status === "pending" ? "owed" : p.status}
                  </span>,
                  p.status === "pending" ? (
                    <button
                      key={`r-${p.id}`}
                      data-testid={`payout-mark-paid-${p.id}`}
                      onClick={() => markPayoutPaid(p.id)}
                      className="h-8 px-3 border border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107] hover:text-black text-xs uppercase tracking-wider font-black transition-colors"
                    >
                      Mark paid
                    </button>
                  ) : (
                    "—"
                  ),
                ])}
              />
            </div>
          </div>
        )}
        {tab === "dashboard" && (
          <div className="border border-white/10 bg-[#141414] p-6">
            <h3 className="font-display text-xl font-black uppercase mb-3">Welcome back</h3>
            <p className="text-zinc-400 text-sm">
              Use the tabs above to view recent leads, orders, and payment transactions. Hit refresh to pull the latest data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const StockEditor = ({ product, onSave }) => {
  const [qty, setQty] = useState(product.stock_qty ?? 0);
  const dirty = qty !== (product.stock_qty ?? 0);
  return (
    <div className="flex items-center gap-2">
      <button
        data-testid={`stock-decrease-${product.id}`}
        onClick={() => setQty(Math.max(0, qty - 1))}
        className="w-8 h-8 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] font-bold"
      >
        −
      </button>
      <input
        data-testid={`stock-input-${product.id}`}
        type="number"
        min="0"
        value={qty}
        onChange={(e) => setQty(Math.max(0, parseInt(e.target.value || 0)))}
        className="w-16 h-8 px-2 text-center bg-[#0a0a0a] border border-white/20 focus:border-[#FFC107] outline-none"
      />
      <button
        data-testid={`stock-increase-${product.id}`}
        onClick={() => setQty(qty + 1)}
        className="w-8 h-8 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] font-bold"
      >
        +
      </button>
      <button
        data-testid={`stock-save-${product.id}`}
        onClick={() => onSave(product.id, qty)}
        disabled={!dirty}
        className={`h-8 px-3 text-xs uppercase tracking-wider font-black transition-colors ${
          dirty ? "bg-[#FFC107] hover:bg-[#FFB300] text-black" : "border border-white/10 text-zinc-600 cursor-not-allowed"
        }`}
      >
        Save
      </button>
    </div>
  );
};

const ORDER_STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const OrderStatusSelect = ({ order, onChange }) => (
  <select
    data-testid={`order-status-select-${order.id}`}
    value={order.status || "pending"}
    onChange={(e) => onChange(order.id, e.target.value)}
    className={`bg-[#0a0a0a] border px-2 py-1.5 text-xs uppercase tracking-wider outline-none cursor-pointer ${
      order.status === "delivered"
        ? "border-green-500/50 text-green-400"
        : order.status === "cancelled"
        ? "border-[#D32F2F]/50 text-[#D32F2F]"
        : order.status === "shipped"
        ? "border-[#FFC107]/50 text-[#FFC107]"
        : "border-white/20"
    }`}
  >
    {ORDER_STATUSES.map((s) => (
      <option key={s} value={s}>{s}</option>
    ))}
  </select>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-white/10 bg-[#141414] p-5" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <Icon size={24} weight="duotone" className="text-[#FFC107] mb-3" />
    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
    <div className="font-display text-2xl font-black">{value}</div>
  </div>
);

const Table = ({ headers, rows, testId }) => (
  <div className="border border-white/10 bg-[#141414] overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-white/10 text-left">
          {headers.map((h) => (
            <th key={h} className="p-3 text-xs uppercase tracking-widest text-zinc-500 font-semibold">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="p-6 text-center text-zinc-500">No records yet.</td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr key={i} className="border-b border-white/5 hover:bg-white/5">
              {r.map((c, j) => (
                <td key={j} className="p-3 align-top">{c}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
